import { useState } from 'react';
import { DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ButtonComponent from 'components/common/ButtonComponent';
import { showAlert } from 'services/alerts.service';
import PropTypes from 'prop-types';

const ImportStickersModal = ({ isOpen, onClose, onImport, isImporting }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPacking, setIsPacking] = useState(false);

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      showAlert({
        text: 'Please select a valid PDF file',
        icon: 'error'
      });
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleImport = () => {
    if (!selectedFile) {
      showAlert({
        text: 'Please select a PDF file to import',
        icon: 'error'
      });
      return;
    }

    onImport(selectedFile, isPacking);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsPacking(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Import Stickers from PDF</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select PDF File</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-green-600">Selected: {selectedFile.name}</p>
            )}
          </div>

          {/* Packing Toggle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="import-packing-checkbox"
              checked={isPacking}
              onChange={e => setIsPacking(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="import-packing-checkbox" className="text-sm font-medium text-gray-700">
              Import as Packing Stickers
            </label>
          </div>

          {/* Description */}
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-sm text-gray-600">
              {isPacking
                ? 'The imported stickers will be marked as packing stickers.'
                : 'The imported stickers will be marked as regular stickers.'}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-6">
          <ButtonComponent onClick={handleClose} variant="secondary" disabled={isImporting}>
            Cancel
          </ButtonComponent>
          <ButtonComponent
            onClick={handleImport}
            disabled={isImporting || !selectedFile}
            className={isImporting ? 'opacity-50 cursor-not-allowed' : ''}
          >
            <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
            {isImporting ? 'Importing...' : 'Import'}
          </ButtonComponent>
        </div>
      </div>
    </div>
  );
};

ImportStickersModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onImport: PropTypes.func,
  isImporting: PropTypes.bool
};

export default ImportStickersModal;
